import React, { useEffect, useRef, useState, useMemo, useContext } from 'react';

import { useParams } from 'react-router-dom';

import { useAppSelector } from 'hooks';
import { usePolicyGroupId } from 'hooks/usePolicyGroupId';
import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';
import { useAutoReformEnabled } from 'hooks/useAutoReformEnabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';

import { apiGetPolicyGroupStatus } from 'api';
import type { APIMultiBinderStatusResponse } from 'types/api.types';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Box,
  useToast,
  Flex,
  HStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  ROLE_ADMIN,
  ROLE_UNDERWRITER,
  WIP_STATUS_APPEAL_APPROVED,
  WIP_STATUS_APPEAL_REJECTED,
  WIP_STATUS_BARA_DECLINED,
  WIP_STATUS_BARA_APPROVED,
  WIP_STATUS_EXPIRED,
  FILE_TYPE,
  BARA_REFERRAL_STATUS_APPROVED,
} from 'variables/constants';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';
import isDeferralAllowed from 'helpers/isDeferralAllowed';
import { APIRemarkFile, APIApplicationFile } from 'types/api.types';

const isFileMissing = (file: APIRemarkFile) =>
  !file.is_deferred && file.status !== 'approved';

interface SharedSubmitProps {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
  data: any;
  remarks: Array<{
    status: string;
    validation_key: string;
    item_key: string;
  }>;
  files: APIRemarkFile[];
  additionalFiles?: APIApplicationFile[];
  missingMandatoryFields: Array<any>;
  isDataSaved: boolean;
  isFormDisabled: boolean;
  user: { role: number };
  changeIsDataSaved: (value: boolean) => void;
  saveApiCall: (
    uuid: string,
    data: any,
    remarks: any,
    files: any,
    isFormComplete: boolean,
  ) => Promise<any>;
  renderFinalSteps?: () => React.ReactNode;
  hasMissingPaymentDetails?: boolean;
  hasMissingDownPaymentDetails?: boolean;
  hasMissingPriorPolicyInfo?: boolean;
  hasSpecialCircumstances?: boolean;
  hasHeatingNotProfessionallyInstalled?: boolean;
  hasNoneLegalApplicantInterest?: boolean;
  hasCommercialRatedVehicle?: boolean;
  hasCommercialOrFarmUse?: boolean;
  hasResidualMarketTakeAllComers?: boolean;
  hasSmapAccessed?: boolean;
  hasApplicantNotFullOwner?: boolean;
  hasLienholderOrLessorSameAsMailing?: boolean;
}

const SharedSubmit: React.FC<SharedSubmitProps> = ({
  formStateKey,
  data,
  remarks,
  files,
  additionalFiles = [],
  missingMandatoryFields,
  isDataSaved,
  isFormDisabled,
  user,
  changeIsDataSaved,
  saveApiCall,
  renderFinalSteps,
  hasMissingPaymentDetails = false,
  hasMissingDownPaymentDetails = false,
  hasMissingPriorPolicyInfo = false,
  hasSpecialCircumstances = false,
  hasHeatingNotProfessionallyInstalled = false,
  hasNoneLegalApplicantInterest = false,
  hasCommercialRatedVehicle = false,
  hasCommercialOrFarmUse = false,
  hasResidualMarketTakeAllComers = false,
  hasSmapAccessed = false,
  hasApplicantNotFullOwner = false,
  hasLienholderOrLessorSameAsMailing = false,
}) => {
  // Extract insurance company for carrier-specific validations
  const insuranceCompany =
    formStateKey === 'autoPersonalForm'
      ? (data as any)?.company_and_broker_information?.insurance_company
      : (data as any)?.policy_information?.insurance_company;

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const bgColor = useColorModeValue('white', 'gray.800');
  const [showSaveBar, setShowSaveBar] = useState(false);
  const appeal = useAppSelector((state) => state[formStateKey].appeal);
  const wipStatus = useAppSelector((state) => state[formStateKey].wip_status);
  const signing = useAppSelector((state) => state[formStateKey].signing);
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');
  const isDlhRequired = useFeatureFlag('enable-dlh-required');
  const isOwnershipOnlyDeferralEnabled = useFeatureFlag(
    'enable-ownership-only-deferral',
  );
  const baraReferral = useAppSelector(
    (state) => state[formStateKey].bara_referral,
  );
  const hasApprovedBaraReferral =
    isBaraEnabled &&
    ((!!baraReferral &&
      baraReferral.status === BARA_REFERRAL_STATUS_APPROVED) ||
      (isBaraFixesEnabled && wipStatus === WIP_STATUS_BARA_APPROVED));
  const hasActivePUAppeal =
    !!appeal && appeal.status !== WIP_STATUS_APPEAL_REJECTED;
  const isReferredToUW = hasApprovedBaraReferral || hasActivePUAppeal;

  const isAutoReformEnabled = useAutoReformEnabled();

  const vehiclesWithMissingAccidentBenefits = useMemo(() => {
    if (!isAutoReformEnabled) return [];
    if (formStateKey !== 'autoPersonalForm') return [];
    const vehicles = (data as any)?.vehicles || [];
    return vehicles
      .map((vehicle: any) => ({
        name:
          [vehicle.model_year, vehicle.make, vehicle.model]
            .filter(Boolean)
            .join(' ') || 'Unknown Vehicle',
        hasMissing:
          vehicle.accident_benefits_questions?.question_1 !== true ||
          vehicle.accident_benefits_questions?.question_2 !== true ||
          vehicle.accident_benefits_questions?.question_3 !== true,
      }))
      .filter((v: any) => v.hasMissing)
      .map((v: any) => v.name);
  }, [data, formStateKey, isAutoReformEnabled]);

  const hasMissingAccidentBenefits =
    vehiclesWithMissingAccidentBenefits.length > 0;

  const policyGroupId = usePolicyGroupId(uuid);
  const { isInMultiBinderGroup } = useGroupedBillingSync(uuid, {
    policyGroupId,
  });

  const { data: multiBinderStatus } = useQuery<APIMultiBinderStatusResponse>({
    queryKey: ['policyGroupStatus', policyGroupId],
    queryFn: () => apiGetPolicyGroupStatus(policyGroupId!),
    enabled: !!policyGroupId && isInMultiBinderGroup,
  });

  const hasIncompleteGroupedPolicies = useMemo(() => {
    if (!isInMultiBinderGroup || !multiBinderStatus) return false;
    if (multiBinderStatus.has_declined_policy) return false;
    return !multiBinderStatus.all_complete;
  }, [isInMultiBinderGroup, multiBinderStatus]);

  const hasDeclinedGroupedPolicy = useMemo(() => {
    if (!isInMultiBinderGroup || !multiBinderStatus) return false;
    if (
      wipStatus === WIP_STATUS_APPEAL_REJECTED ||
      wipStatus === WIP_STATUS_BARA_DECLINED
    )
      return false;

    return multiBinderStatus.has_declined_policy;
  }, [isInMultiBinderGroup, multiBinderStatus, wipStatus]);

  const failedValidations = validations.filter(
    (v) => v.validation_outcome === 'fail',
  );

  const missingRemarks = remarks.filter((remark) => {
    if (remark.status === 'approved') return false;
    if (remark.validation_key === '133b' && remark.status === 'failed') {
      return false;
    }
    return true;
  });

  const hasFailedValidations = useMemo(() => {
    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED) return false;
    if (hasApprovedBaraReferral) return false;
    return failedValidations.length > 0;
  }, [failedValidations, appeal, hasApprovedBaraReferral]);

  const hasMissingFields = useMemo(() => {
    if (missingMandatoryFields.length > 0) return true;
    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED) return false;
    if (hasApprovedBaraReferral) return false;
    if (missingRemarks.length > 0) return true;
    return false;
  }, [missingMandatoryFields, missingRemarks, appeal, hasApprovedBaraReferral]);

  const hasMissingMandatoryFiles = useMemo(() => {
    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED) return false;
    if (hasApprovedBaraReferral) return false;

    const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

    // Add signed refusal check for auto forms with Facility carrier
    if (
      formStateKey === 'autoPersonalForm' &&
      insuranceCompany?.toLowerCase().includes('facility')
    ) {
      const signedRefusalFile = additionalFiles?.find(
        (f) =>
          f.file_type === FILE_TYPE.TYPE_FACILITY_REFUSAL_LETTER_FOR_AUTO.type,
      );
      if (!signedRefusalFile) {
        return true; // Missing mandatory file
      }
    }

    const hasMissingStaticMandatoryFile = mandatoryFileValidations.some(
      (validationKey) => {
        const file = files.find((f) => f.validation_key === validationKey);
        if (!file) return false;
        if (file.is_deferred) return false;
        return file.status !== 'approved';
      },
    );

    const hasMissingBindRequiredFile =
      isOwnershipOnlyDeferralEnabled &&
      files.some(
        (f) =>
          !isDeferralAllowed(
            f.validation_key,
            validations,
            isOwnershipOnlyDeferralEnabled,
          ) && isFileMissing(f),
      );

    const hasMissingTrainingCertificate =
      isDlhRequired &&
      files.some(
        (f) =>
          f.validation_key === 'driver_training_certificate' &&
          f.mandatory === true &&
          !f.is_deferred &&
          f.status !== 'approved',
      );

    const hasMissing133bFile = files.some(
      (f) =>
        f.validation_key === '133b' &&
        f.mandatory === true &&
        !f.is_deferred &&
        f.status !== 'approved',
    );

    return (
      hasMissingStaticMandatoryFile ||
      hasMissingBindRequiredFile ||
      hasMissingTrainingCertificate ||
      hasMissing133bFile
    );
  }, [
    files,
    formStateKey,
    insuranceCompany,
    additionalFiles,
    appeal,
    hasApprovedBaraReferral,
    isDlhRequired,
    validations,
    isOwnershipOnlyDeferralEnabled,
  ]);

  // Calculate form completion status for backend
  const isFormComplete = useMemo(() => {
    if (hasFailedValidations) return false;
    if (hasMissingFields) return false;
    if (hasMissingPriorPolicyInfo) return false;
    if (hasSpecialCircumstances && !isReferredToUW) return false;
    if (hasHeatingNotProfessionallyInstalled && !isReferredToUW) return false;
    if (hasCommercialOrFarmUse && !isReferredToUW) return false;
    if (hasResidualMarketTakeAllComers && !isReferredToUW) return false;
    if (hasSmapAccessed && !isReferredToUW) return false;
    if (hasApplicantNotFullOwner && !isReferredToUW) return false;
    if (hasNoneLegalApplicantInterest) return false;
    if (hasLienholderOrLessorSameAsMailing) return false;
    if (hasMissingMandatoryFiles) return false;
    if (hasMissingAccidentBenefits) return false;
    return true;
  }, [
    hasFailedValidations,
    hasMissingFields,
    hasMissingPriorPolicyInfo,
    hasSpecialCircumstances,
    hasHeatingNotProfessionallyInstalled,
    hasCommercialOrFarmUse,
    hasResidualMarketTakeAllComers,
    hasSmapAccessed,
    hasApplicantNotFullOwner,
    hasNoneLegalApplicantInterest,
    hasLienholderOrLessorSameAsMailing,
    hasMissingMandatoryFiles,
    hasMissingAccidentBenefits,
    isReferredToUW,
  ]);

  const saveApplicationLogMutation = useMutation({
    mutationFn: () => {
      return saveApiCall(uuid!, data, remarks, files, isFormComplete);
    },
    onSuccess: async () => {
      changeIsDataSaved(true);

      if (policyGroupId) {
        await queryClient.refetchQueries({
          queryKey: ['policyGroupStatus', policyGroupId],
        });
      }

      toast({
        title: 'Changes saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      console.error('Failed to save application log:', error);
      const firstErrorObj = error?.error?.[0];
      const nestedError =
        firstErrorObj && typeof firstErrorObj === 'object'
          ? (Object.values(firstErrorObj)[0] as string)
          : undefined;
      const errorMsg = isBaraFixesEnabled
        ? error?.detail ||
          nestedError ||
          (typeof error?.error === 'string' ? error.error : undefined) ||
          error?.message
        : nestedError;
      toast({
        title: 'Error saving changes',
        description: errorMsg || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // After a BARA approval the page is locked except billing, which can still be saved
  const canSave =
    !isFormDisabled ||
    (isBaraFixesEnabled &&
      hasApprovedBaraReferral &&
      signing === null &&
      wipStatus !== WIP_STATUS_EXPIRED &&
      user.role !== ROLE_UNDERWRITER);

  // Save data 1 minute after it was last changed
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    if (!isDataSaved && canSave) {
      autoSaveTimerRef.current = setTimeout(() => {
        saveApplicationLogMutation.mutate();
      }, 60000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDataSaved, canSave]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSaveBar(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const shouldDisableSave = () => {
    if (saveApplicationLogMutation.isPending) return true;

    if (user.role === ROLE_ADMIN) return isDataSaved;

    return isDataSaved;
  };

  const showFinalSteps = () => {
    if (signing !== null) return true;

    if (!isDataSaved && canSave) return false;

    if (user.role === ROLE_ADMIN) return true;

    if (hasIncompleteGroupedPolicies) return false;
    if (hasDeclinedGroupedPolicy) return false;

    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED && !hasMissingFields)
      return true;

    if (hasApprovedBaraReferral && !isBaraFixesEnabled && !hasMissingFields)
      return true;

    if (hasApprovedBaraReferral && isBaraFixesEnabled)
      return (
        !hasMissingFields &&
        !hasMissingPaymentDetails &&
        !hasMissingDownPaymentDetails
      );

    return (
      !hasFailedValidations &&
      !hasMissingFields &&
      !hasMissingPaymentDetails &&
      !hasMissingDownPaymentDetails &&
      !hasMissingPriorPolicyInfo &&
      (!hasSpecialCircumstances || isReferredToUW) &&
      (!hasHeatingNotProfessionallyInstalled || isReferredToUW) &&
      !hasNoneLegalApplicantInterest &&
      !hasLienholderOrLessorSameAsMailing &&
      !hasMissingMandatoryFiles &&
      !hasCommercialRatedVehicle &&
      (!hasCommercialOrFarmUse || isReferredToUW) &&
      (!hasResidualMarketTakeAllComers || isReferredToUW) &&
      (!hasSmapAccessed || isReferredToUW) &&
      (!hasApplicantNotFullOwner || isReferredToUW) &&
      !hasMissingAccidentBenefits
    );
  };

  const showErrors =
    hasMissingPaymentDetails ||
    hasMissingDownPaymentDetails ||
    hasMissingPriorPolicyInfo ||
    (hasSpecialCircumstances && !isReferredToUW) ||
    (hasHeatingNotProfessionallyInstalled && !isReferredToUW) ||
    hasCommercialRatedVehicle ||
    (hasCommercialOrFarmUse && !isReferredToUW) ||
    (hasResidualMarketTakeAllComers && !isReferredToUW) ||
    (hasSmapAccessed && !isReferredToUW) ||
    (hasApplicantNotFullOwner && !isReferredToUW) ||
    hasNoneLegalApplicantInterest ||
    hasLienholderOrLessorSameAsMailing ||
    hasMissingFields ||
    !isDataSaved ||
    hasFailedValidations ||
    hasMissingMandatoryFiles ||
    hasMissingAccidentBenefits ||
    hasIncompleteGroupedPolicies ||
    hasDeclinedGroupedPolicy ||
    hasCommercialRatedVehicle;

  if (wipStatus === WIP_STATUS_EXPIRED) {
    return (
      <Card mb="20px">
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Application Expired</AlertTitle>
            <AlertDescription>
              This application has expired and can no longer be edited.
            </AlertDescription>
          </Box>
        </Alert>
      </Card>
    );
  }

  return (
    <>
      {showErrors && (
        <>
          <Card mb="20px">
            <Flex gap="20px" direction="row">
              {hasMissingFields && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Missing fields</AlertTitle>
                    <AlertDescription>
                      Generating binder will not be available until all missing
                      fields and remarks are filled.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasMissingPaymentDetails && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Missing payment method details</AlertTitle>
                    <AlertDescription>
                      Customer has not filled the payment method details. Please
                      send the secure payment link for the payment method and
                      wait for the customer to complete payment information.
                      This information is required for the PAF form and billing.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasMissingDownPaymentDetails && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Missing down payment details</AlertTitle>
                    <AlertDescription>
                      Customer has not filled the down payment details. Please
                      send the secure payment link for the down payment and wait
                      for the customer to complete payment information.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasMissingPriorPolicyInfo && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Missing previous policy information</AlertTitle>
                    <AlertDescription>
                      The application indicates prior insurance history but no
                      history has been included.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasFailedValidations && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Failed validations</AlertTitle>
                    <AlertDescription>
                      There are failed validations that need to be addressed.
                      {validations.map((v) =>
                        v.validation_outcome === 'fail' ? (
                          <Box key={v.validation_id} fontSize="sm">
                            - {v.validation_message}
                          </Box>
                        ) : null,
                      )}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasSpecialCircumstances && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Has special circumstances</AlertTitle>
                    <AlertDescription>
                      Due to "Are there special circumstances regarding this
                      application which the company should know?" question being
                      answered as Yes, you can't proceed to printing the binder.
                      Please refer to BIG Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      .
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasResidualMarketTakeAllComers && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Residual market (take-all-comers)</AlertTitle>
                    <AlertDescription>
                      This risk is eligible for the residual market but is being
                      placed in the regular market under the take-all-comers
                      rule. You can't proceed to generating the binder. Please
                      refer to BIG Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      .
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasSmapAccessed && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>SMAP accessed</AlertTitle>
                    <AlertDescription>
                      The Supplementary Market Availability Plan (SMAP) was
                      accessed to place this risk. You can't proceed to
                      generating the binder. Please refer to BIG Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      .
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasHeatingNotProfessionallyInstalled && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      Heating system not professionally installed
                    </AlertTitle>
                    <AlertDescription>
                      The heating system is not professionally installed. Please
                      refer to BIG Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      . You cannot proceed to generating the binder until this
                      is resolved
                      {isBaraEnabled ? ' or a referral is active' : ''}.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasNoneLegalApplicantInterest && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Legal ownership</AlertTitle>
                    <AlertDescription>
                      Only individuals who hold <strong>legal ownership</strong>{' '}
                      and a financial interest in the dwelling should be listed
                      as <strong>Named Insureds</strong>. If an individual does{' '}
                      <strong>not</strong> legally own the dwelling (i.e., is
                      not listed on deed/title), remove them as a Named Insured.
                      <br />
                      <br />
                      <strong>Note:</strong> Any credit consent obtained from
                      this individual must be re-quoted and applied solely to a
                      Named Insured.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasMissingMandatoryFiles && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Missing mandatory files</AlertTitle>
                    <AlertDescription>
                      Mandatory file uploads are required before generating the
                      binder. Please upload and validate all required documents.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasMissingAccidentBenefits && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Accident Benefits Questions</AlertTitle>
                    <AlertDescription>
                      The following vehicle(s){' '}
                      <i>
                        {vehiclesWithMissingAccidentBenefits.length === 1
                          ? vehiclesWithMissingAccidentBenefits[0]
                          : vehiclesWithMissingAccidentBenefits
                              .map((v: string, i: number) => `${i + 1}) ${v}`)
                              .join(', ')}
                      </i>{' '}
                      indicate that the customer has not been informed of the
                      changes to the Accident Benefits Auto Reform as of July 1,
                      2026. Please contact the customer to explain the changes,
                      confirm that the information has been communicated, and
                      resubmit once complete.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasIncompleteGroupedPolicies && (
                <Alert status="warning">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Multi-Binder Group Incomplete</AlertTitle>
                    <AlertDescription>
                      This binder is part of a multi-binder group. All linked
                      binders must be complete before generating documents.
                      Please complete all applications in this group.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasDeclinedGroupedPolicy && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Declined Binder in Group</AlertTitle>
                    <AlertDescription>
                      A declined binder is combined with this submission. Unlink
                      the declined binder to proceed with the approved
                      binder(s).
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasLienholderOrLessorSameAsMailing && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Lienholder/Lessor Address</AlertTitle>
                    <AlertDescription>
                      A lienholder or lessor address is the same as the mailing
                      address. Please enter the lienholder or lessor's own
                      address in the Vehicles section.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasCommercialRatedVehicle && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Commercial Rated Vehicle</AlertTitle>
                    <AlertDescription>
                      The vehicle being rated is classified as a commercially
                      rated vehicle. Please submit through the BIG Commercial
                      Lines workflow or refer to BIG Underwriting for further
                      guidance.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasCommercialOrFarmUse && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Commercial or Farm Use</AlertTitle>
                    <AlertDescription>
                      A vehicle is marked for commercial or farm use. Please
                      refer this application to BIG Portfolio Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      . You cannot proceed to generating the binder until this
                      is resolved
                      {isBaraEnabled ? ' or a referral is active' : ''}.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {hasApplicantNotFullOwner && !isReferredToUW && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Applicant is not the full owner</AlertTitle>
                    <AlertDescription>
                      The applicant is not both the registered owner and the
                      actual owner of the described automobile(s). Please refer
                      to BIG Portfolio Underwriting
                      {isBaraEnabled
                        ? ' or submit an After-Hours Referral (MP/Delegate)'
                        : ''}
                      . You cannot proceed to generating the binder until this
                      is resolved
                      {isBaraEnabled ? ' or a referral is active' : ''}.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {!isDataSaved && !shouldDisableSave() && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Unsaved change</AlertTitle>
                    <AlertDescription>
                      You have unsaved changes.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </Flex>
          </Card>
        </>
      )}

      {!showSaveBar && <Card textAlign="center">Preparing form...</Card>}

      {showFinalSteps() && renderFinalSteps && (
        <Card mb="20px">{renderFinalSteps()}</Card>
      )}

      {showSaveBar && canSave && !isDataSaved && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          maxW="500px"
        >
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack spacing={6}>
              <Text>You have unsaved changes</Text>
              <Button
                onClick={() => saveApplicationLogMutation.mutate()}
                disabled={shouldDisableSave()}
                isLoading={saveApplicationLogMutation.isPending}
                colorScheme="yellow"
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}

      {isFormDisabled && (
        <Card mb="20px">
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>Read Only</AlertTitle>
              <AlertDescription>
                At this stage binder is no longer editable
              </AlertDescription>
            </Box>
          </Alert>
        </Card>
      )}
    </>
  );
};

export default SharedSubmit;
